import type { AnchorHTMLAttributes } from "react";
import { pxToRem } from "@mos-cat/ds-theme";
import { createComponent, createHook } from "reakit-system";
import type { SystemStyleObject } from "@styled-system/css";
import type { StylesOptions } from "../../hooks";
import { useStyles, useFocusable } from "../../hooks";
import type { Kind, VertSize } from "./variants";
import { kind as getKind, vertSize as getVertSize } from "./variants";
import { WHCM, WHCMstyles } from "../../constants";

export type NavLinkOptions = {
  kind: Kind;
  vertSize: VertSize;
} & StylesOptions;

export type NavLinkHTMLProps = AnchorHTMLAttributes<HTMLAnchorElement>;
export type NavLinkProps = NavLinkOptions & NavLinkHTMLProps;

export const NAVIGATION_WRAPPER_STYLES = {
  borderBottom: 1,
  borderBottomColor: "navListBorderBottomColor",
  ul: {
    p: 0,
    li: {
      display: "flex",
    },
  },
} as SystemStyleObject;

export const NAVIGATION_STACK_LAYOUT_STYLES = {
  borderBottom: 1,
  borderBottomColor: "navListBorderBottomColor",
  padding: 0,
  li: {
    display: "flex",
  },
  a: {
    width: 1,
  },
} as SystemStyleObject;

export const NAVIGATION_REEL_LAYOUT_STYLES = {
  m: "-sp-4",
  ul: {
    display: "flex",
    padding: pxToRem(4),
    paddingBottom: pxToRem(5),
    position: "relative",
    minWidth: 1,
    "&:before": {
      content: '""',
      position: "absolute",
      width: "auto",
      bottom: pxToRem(4),
      left: pxToRem(4),
      right: pxToRem(4),
      height: pxToRem(1),
      backgroundColor: "navListBorderBottomColor",
      [WHCM]: {
        backgroundColor: "WindowText",
      },
    },
  },
  li: {
    display: "flex",
    position: "relative",
  },
  "li + li": {
    marginLeft: "sp-4",
  },
} as SystemStyleObject;

let currentLinkSelector = [
  '&[aria-current]:not([aria-current="false"]):not([aria-current=""])',
  '&[aria-current]:not([aria-current="false"]):not([aria-current=""]):hover',
  '&[aria-current]:not([aria-current="false"]):not([aria-current=""]):active',
];

export const useNavLink = createHook<NavLinkOptions, NavLinkHTMLProps>({
  name: "NavLink",
  keys: ["kind", "vertSize"],
  compose: [useStyles],
  useOptions(options) {
    return {
      ...options,
      styles: {
        ...options.styles,
        padding: 0,
        marginBottom: pxToRem(-1),
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        height: "100%",
        ...getKind(options),
        ...getVertSize(options),

        ":focus, :focus-visible": {
          outline: 0,
          boxShadow: "focus",
          borderRadius: 4,
        },
        [WHCM]: {
          border: 0,

          ":focus, :focus-visible": {
            outline: "auto",
            outlineWidth: "0.25rem",
          },

          [currentLinkSelector.join(",")]: {
            backgroundColor: "WindowText",
            color: "Window",
          },
        },
      } as SystemStyleObject,
    };
  },
});

export const NavLink = createComponent<"a", NavLinkOptions>({
  useHook: useNavLink,
  as: "a",
  memo: true,
});
